import { ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Modal, Typography } from "antd"
import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useOutletContext } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/routes.ts"
import type { RootState } from "../../../../store.ts"
import { distinctFolderNames } from "../../helpers/reportFolders.ts"
import { useReportSettingsSubmit } from "../../hooks/useReportSettingsSubmit.ts"
import { ReportSettingsModal } from "../ReportSettingsModal.tsx"
import { ReportsEmpty } from "../ReportsEmpty.tsx"
import { ReportsList } from "../ReportsList.tsx"
import {
  deleteReport,
  duplicateReport,
  toggleReportFavorite
} from "../../redux/reports.thunks.ts"
import type { ReportListItem } from "../../types/types.ts"
import type { ReportsShellOutletContext } from "./ReportsShell.tsx"

const { Title, Text } = Typography

const NAV_TITLE_KEYS = {
  all: "reports.rail.nav.all",
  favorites: "reports.rail.nav.favorites",
  recent: "reports.rail.nav.recent",
  shared: "reports.rail.nav.shared"
} as const

const ReportsHomePage = ({
  results,
  fetching,
  filters,
  toggleFavorite,
  duplicateReport,
  deleteReport
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeNav } = useOutletContext<ReportsShellOutletContext>()
  const [settingsTarget, setSettingsTarget] = useState<ReportListItem | null>(
    null
  )
  const folderOptions = useMemo(() => distinctFolderNames(results), [results])
  const handleSettingsSubmit = useReportSettingsSubmit(settingsTarget?.id)

  const visibleReports = useMemo(() => {
    switch (activeNav) {
      case "favorites":
        return results.filter((report) => report.is_favorite)
      case "shared":
        return results.filter(
          (report) => (report.access_level ?? "owner") !== "owner"
        )
      case "recent":
        return [...results].sort((a, b) =>
          b.updated_at.localeCompare(a.updated_at)
        )
      default:
        return results
    }
  }, [results, activeNav])

  const handleOpen = useCallback(
    (report: ReportListItem) =>
      navigate(RoutesPaths.reports.view(report.id)),
    [navigate]
  )

  const handleToggleFavorite = useCallback(
    (report: ReportListItem) =>
      toggleFavorite({ id: report.id, previousIsFavorite: report.is_favorite }),
    [toggleFavorite]
  )

  const handleDuplicate = useCallback(
    (report: ReportListItem) =>
      duplicateReport({
        id: report.id,
        name: report.name,
        description: report.description,
        tags: report.tags,
        visibility: report.visibility
      }),
    [duplicateReport]
  )

  const handleDelete = useCallback(
    (report: ReportListItem) => {
      Modal.confirm({
        title: t("reports.deleteConfirm.title"),
        content: t("reports.deleteConfirm.body", { name: report.name }),
        icon: <ExclamationCircleOutlined />,
        okText: t("reports.deleteConfirm.confirm"),
        okButtonProps: { danger: true },
        cancelText: t("reports.cancel"),
        onOk: () => deleteReport({ id: report.id })
      })
    },
    [deleteReport, t]
  )

  const hasFilters = Boolean(filters.search) || activeNav !== "all"
  const showEmpty = !fetching && visibleReports.length === 0

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
        <Flex vertical>
          <Title level={3} className="m-0 text-start">
            {t(NAV_TITLE_KEYS[activeNav])}
          </Title>
          <Text type="secondary">{t("reports.subtitle")}</Text>
        </Flex>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate(RoutesPaths.reports.create)}
        >
          {t("reports.addNewReport")}
        </Button>
      </Flex>

      {showEmpty ? (
        <ReportsEmpty
          hasFilters={hasFilters}
          onCreate={() => navigate(RoutesPaths.reports.create)}
        />
      ) : (
        <ReportsList
          selectionFilters={{ ...filters, activeNav }}
          reports={visibleReports}
          loading={fetching}
          onOpen={handleOpen}
          onToggleFavorite={handleToggleFavorite}
          onSettings={setSettingsTarget}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
      )}

      {settingsTarget && (
        <ReportSettingsModal
          report={settingsTarget}
          folderOptions={folderOptions}
          onSubmit={handleSettingsSubmit}
          onClose={() => setSettingsTarget(null)}
        />
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  results: state.reports.results,
  fetching: state.reports.fetching,
  filters: state.reports.filters
})

const mapDispatchToProps = {
  toggleFavorite: toggleReportFavorite,
  duplicateReport,
  deleteReport
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ReportsHomePage)
